/**
 * Cross-platform modifier helpers. On macOS the "command" modifier is Meta
 * (⌘); everywhere else it's Control. We detect mac defensively so SSR / jsdom
 * (no `navigator`) simply falls back to the Ctrl convention.
 */

/** Whether we're running on macOS (best-effort; safe when `navigator` is absent). */
export function isMac(): boolean {
  if (typeof navigator === 'undefined') return false;
  // `platform` is the most reliable when present; fall back to UA sniffing.
  const platform = navigator.platform ?? '';
  if (platform) return /mac|iphone|ipad|ipod/i.test(platform);
  return /mac|iphone|ipad|ipod/i.test(navigator.userAgent ?? '');
}

/**
 * Whether the platform "command" modifier is pressed for a keyboard event,
 * AND the *other* platform's modifier is NOT — so a Ctrl+key on macOS (or a
 * Meta/⊞+key on Windows/Linux) is ignored rather than double-firing.
 */
export function modPressed(e: Pick<KeyboardEvent, 'metaKey' | 'ctrlKey'>): boolean {
  return isMac() ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
}

/**
 * A human-readable label for a "command modifier + key" shortcut, formatted for
 * the current platform. macOS uses the symbol form (⌘O, ⇧⌘O); everywhere else
 * the spelled-out form (Ctrl+O, Ctrl+Shift+O). The `key` is shown upper-cased.
 * Agrees with `modPressed` (Meta on mac, Ctrl elsewhere) so the displayed hint
 * matches the binding that actually fires.
 */
export function shortcutLabel(key: string, opts?: { shift?: boolean }): string {
  const shift = opts?.shift === true;
  const k = key.toUpperCase();
  if (isMac()) {
    // Modifier order follows Apple's convention: ⌃⌥⇧⌘ → here just ⇧ then ⌘.
    return `${shift ? '⇧' : ''}⌘${k}`;
  }
  return `Ctrl+${shift ? 'Shift+' : ''}${k}`;
}

/** Label for the view-switch chord: ⌃⌘<n> on macOS, Ctrl+Shift+<n> elsewhere. */
export function viewSwitchLabel(key: string): string {
  return isMac() ? `⌃⌘${key.toUpperCase()}` : `Ctrl+Shift+${key.toUpperCase()}`;
}

/**
 * Label for a bare (no-modifier) key shortcut, formatted with the symbol
 * Mac users expect when one exists (e.g. Delete → ⌫, Enter → ↵) and the
 * spelled-out name everywhere else. Used by context menus to surface the
 * keys their parent component already binds.
 */
export function keyLabel(key: string): string {
  const mac = isMac();
  switch (key) {
    case 'Backspace':
    case 'Delete':
      return mac ? '⌫' : 'Del';
    case 'Enter':
    case 'Return':
      return mac ? '↵' : 'Enter';
    case 'Tab':
      return mac ? '⇥' : 'Tab';
    case 'Escape':
      return mac ? '⎋' : 'Esc';
    case 'Space':
      return mac ? '␣' : 'Space';
    default:
      // F-keys and ordinary letter keys pass through unchanged.
      return key;
  }
}
